import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import {
  MapPin, Crosshair, MagnifyingGlass, X, CaretDown, Check, CaretRight,
} from "@phosphor-icons/react";

/**
 * FarmersPulse location bar — persistent geo selector for the standalone
 * FarmersPulse header. Complements NaturePulse's existing LocationPill by
 * exposing Country / State / City / Village as top-of-page breadcrumbs.
 *
 * Shares storage key with LocationPill (`np:user_location`) so any change
 * here propagates to every other surface (GlobalFarmingCountryPicker,
 * FarmersToday weather, LocalAlertBanner, etc) via `np:location-changed`.
 */
const STORAGE_KEY = "np:user_location";

// Reasonable primary set — country picker also allows free-text search.
export const FP_COUNTRIES = [
  { code: "IN", flag: "🇮🇳", name: "India" },
  { code: "US", flag: "🇺🇸", name: "United States" },
  { code: "BR", flag: "🇧🇷", name: "Brazil" },
  { code: "CN", flag: "🇨🇳", name: "China" },
  { code: "MX", flag: "🇲🇽", name: "Mexico" },
  { code: "ID", flag: "🇮🇩", name: "Indonesia" },
  { code: "NG", flag: "🇳🇬", name: "Nigeria" },
  { code: "KE", flag: "🇰🇪", name: "Kenya" },
  { code: "VN", flag: "🇻🇳", name: "Vietnam" },
  { code: "TH", flag: "🇹🇭", name: "Thailand" },
  { code: "PH", flag: "🇵🇭", name: "Philippines" },
  { code: "BD", flag: "🇧🇩", name: "Bangladesh" },
  { code: "PK", flag: "🇵🇰", name: "Pakistan" },
  { code: "LK", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "NP", flag: "🇳🇵", name: "Nepal" },
  { code: "AR", flag: "🇦🇷", name: "Argentina" },
  { code: "CL", flag: "🇨🇱", name: "Chile" },
  { code: "CO", flag: "🇨🇴", name: "Colombia" },
  { code: "PE", flag: "🇵🇪", name: "Peru" },
  { code: "ES", flag: "🇪🇸", name: "Spain" },
  { code: "IT", flag: "🇮🇹", name: "Italy" },
  { code: "FR", flag: "🇫🇷", name: "France" },
  { code: "DE", flag: "🇩🇪", name: "Germany" },
  { code: "NL", flag: "🇳🇱", name: "Netherlands" },
  { code: "GB", flag: "🇬🇧", name: "United Kingdom" },
  { code: "IE", flag: "🇮🇪", name: "Ireland" },
  { code: "AU", flag: "🇦🇺", name: "Australia" },
  { code: "NZ", flag: "🇳🇿", name: "New Zealand" },
  { code: "ZA", flag: "🇿🇦", name: "South Africa" },
  { code: "ET", flag: "🇪🇹", name: "Ethiopia" },
  { code: "EG", flag: "🇪🇬", name: "Egypt" },
  { code: "GH", flag: "🇬🇭", name: "Ghana" },
  { code: "TZ", flag: "🇹🇿", name: "Tanzania" },
  { code: "UG", flag: "🇺🇬", name: "Uganda" },
  { code: "RW", flag: "🇷🇼", name: "Rwanda" },
  { code: "MA", flag: "🇲🇦", name: "Morocco" },
  { code: "TR", flag: "🇹🇷", name: "Turkey" },
  { code: "SA", flag: "🇸🇦", name: "Saudi Arabia" },
  { code: "AE", flag: "🇦🇪", name: "UAE" },
  { code: "CA", flag: "🇨🇦", name: "Canada" },
  { code: "JP", flag: "🇯🇵", name: "Japan" },
  { code: "KR", flag: "🇰🇷", name: "South Korea" },
  { code: "MY", flag: "🇲🇾", name: "Malaysia" },
  { code: "SG", flag: "🇸🇬", name: "Singapore" },
];

function writeLocation(loc) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loc)); } catch {}
  window.dispatchEvent(new CustomEvent("np:location-changed", { detail: loc }));
}

function useLoc() {
  const [loc, setLoc] = useState(readLocation);
  useEffect(() => {
    const onChange = () => setLoc(readLocation());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) onChange(); };
    window.addEventListener("np:location-changed", onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("np:location-changed", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return loc;
}

function shortLabel(loc) {
  if (!loc) return "Set location";
  const parts = [loc.city || loc.village, loc.country].filter(Boolean);
  return parts.join(", ") || "Set location";
}

/* ----------------------------- Pill (header) ----------------------------- */

export function FarmersLocationPill() {
  const loc = useLoc();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="farmers-location-pill"
        title="Change your location"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#0F3D2A]/25 bg-white/70 hover:bg-white transition px-3 py-1 text-[#0F3D2A]"
      >
        <MapPin size={13} weight="fill" />
        <span className="max-w-[220px] truncate font-serif-display text-sm leading-none">
          {shortLabel(loc)}
        </span>
        <span className="inline-flex items-center gap-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase opacity-70">
          Change <CaretDown size={9} weight="bold" />
        </span>
      </button>
      {open && <LocationEditModal current={loc} onClose={() => setOpen(false)} />}
    </>
  );
}

/* ---------------------- Breadcrumb bar (below header) --------------------- */

export function FarmersLocationBreadcrumb() {
  const loc = useLoc();
  const [open, setOpen] = useState(false);

  const crumbs = useMemo(() => {
    const c = loc?.country;
    const s = loc?.state;
    const ci = loc?.city;
    const v = loc?.village;
    return [
      { label: "🌍 World", muted: !!c },
      c && { label: c, muted: !!s },
      s && { label: s, muted: !!ci },
      ci && { label: ci, muted: !!v },
      v && { label: v, muted: false },
    ].filter(Boolean);
  }, [loc]);

  return (
    <>
      <div
        data-testid="farmers-location-breadcrumb"
        className="fixed top-[68px] md:top-[76px] inset-x-0 z-30 bg-[#0F3D2A] text-[#F8F4E9] border-b border-[#0F3D2A]"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-1.5 flex items-center gap-2 text-sm">
          <MapPin size={12} weight="fill" className="text-[#F8F4E9]/80" />
          <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto scrollbar-none">
            {crumbs.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1 whitespace-nowrap">
                <span
                  className={`font-serif-display leading-none ${
                    c.muted ? "text-[#F8F4E9]/60" : "text-[#F8F4E9] font-semibold"
                  }`}
                  data-testid={`farmers-loc-crumb-${i}`}
                >
                  {c.label}
                </span>
                {i < crumbs.length - 1 && (
                  <CaretRight size={9} weight="bold" className="text-[#F8F4E9]/50" />
                )}
              </span>
            ))}
            {crumbs.length === 1 && (
              <span className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#F8F4E9]/70">
                — set your location for local weather, alerts, harvests
              </span>
            )}
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="farmers-loc-change"
            className="rounded-full border border-[#F8F4E9]/30 bg-transparent hover:bg-[#F8F4E9]/10 px-3 py-0.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase"
          >
            Change
          </button>
        </div>
      </div>
      {open && <LocationEditModal current={loc} onClose={() => setOpen(false)} />}
    </>
  );
}

/* ------------------------------ Edit modal ------------------------------- */

function LocationEditModal({ current, onClose }) {
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState(current?.country || "");
  const [state, setState] = useState(current?.state || "");
  const [city, setCity] = useState(current?.city || "");
  const [village, setVillage] = useState(current?.village || "");
  const [lat, setLat] = useState(current?.lat);
  const [lon, setLon] = useState(current?.lon);
  const [countryQuery, setCountryQuery] = useState("");
  const [placeQuery, setPlaceQuery] = useState("");
  const [placeResults, setPlaceResults] = useState([]);
  const dialogRef = useRef(null);

  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return FP_COUNTRIES;
    return FP_COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [countryQuery]);

  useEffect(() => {
    const on = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);

  const useGPS = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not available");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const glat = pos.coords.latitude, glon = pos.coords.longitude;
      try {
        const { data } = await http.post("/nature/geo/lookup", { lat: glat, lon: glon });
        setLat(glat); setLon(glon);
        setCountry(data.country || "");
        setState(data.state || "");
        setCity(data.city || data.locality || "");
        setVillage(data.village || "");
        toast.success("Location detected — review and Save");
      } catch (e) {
        toast.error("Reverse geocode failed");
      } finally { setBusy(false); }
    }, (err) => {
      setBusy(false);
      toast.error(err.code === 1 ? "Permission denied — enable location in your browser" : "Could not read your location");
    }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 3600000 });
  };

  const searchPlace = async () => {
    if (placeQuery.trim().length < 3) return;
    setBusy(true);
    try {
      const q = encodeURIComponent(placeQuery.trim());
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&addressdetails=1&q=${q}`);
      const arr = await r.json();
      setPlaceResults(arr || []);
    } catch { toast.error("Search failed"); }
    finally { setBusy(false); }
  };

  const pickPlace = (r) => {
    const a = r.address || {};
    setLat(parseFloat(r.lat));
    setLon(parseFloat(r.lon));
    setCountry(a.country || "");
    setState(a.state || a.region || "");
    setCity(a.city || a.town || a.municipality || "");
    setVillage(a.village || a.hamlet || a.suburb || "");
    setPlaceResults([]);
    setPlaceQuery("");
  };

  const save = async () => {
    if (!country && !state && !city && !village) {
      toast.error("Please choose a country or a place");
      return;
    }
    let finalLat = lat, finalLon = lon;
    // If we don't yet have coords (typed input or country-grid pick), try
    // a Nominatim geocode of the entered name so FarmersPulseHome (which
    // keys off lat/lon) can fetch calendar + nearby data for the new spot.
    if (finalLat == null || finalLon == null) {
      const q = [village, city, state, country].filter(Boolean).join(", ");
      setBusy(true);
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(q)}`);
        const arr = await r.json();
        if (arr && arr[0]) {
          finalLat = parseFloat(arr[0].lat);
          finalLon = parseFloat(arr[0].lon);
        }
      } catch { /* ignore — save anyway without coords */ }
      finally { setBusy(false); }
    }
    const display_name = [village, city, state, country].filter(Boolean).join(", ");
    const next = {
      lat: finalLat ?? null,
      lon: finalLon ?? null,
      country: country || null,
      state: state || null,
      city: city || null,
      village: village || null,
      display_name,
      source: (finalLat && finalLon) ? "picker" : "manual",
    };
    writeLocation(next);
    toast.success(`Location set to ${display_name || country}`);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-start justify-center px-4 pt-16 md:pt-24 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="farmers-loc-modal"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg bg-[#F8F4E9] rounded-3xl border border-[#0F3D2A]/15 shadow-2xl overflow-hidden mb-16"
      >
        <div className="bg-[#0F3D2A] text-[#F8F4E9] px-5 py-3 flex items-center gap-2">
          <MapPin size={15} weight="fill" />
          <div className="flex-1 font-mono-tech text-[10px] tracking-[0.32em] uppercase">
            Your Location
          </div>
          <button
            onClick={onClose}
            data-testid="farmers-loc-modal-close"
            className="rounded-full p-1.5 hover:bg-[#F8F4E9]/10"
            aria-label="Close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* GPS + Search row */}
          <div className="grid sm:grid-cols-2 gap-2">
            <button
              onClick={useGPS}
              disabled={busy}
              data-testid="farmers-loc-gps"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2D8659] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#0F3D2A] disabled:opacity-40"
            >
              <Crosshair size={12} weight="fill" /> {busy ? "Locating…" : "Use my GPS"}
            </button>
            <div className="flex gap-1">
              <input
                value={placeQuery}
                onChange={(e) => setPlaceQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchPlace()}
                placeholder="Search city, village…"
                data-testid="farmers-loc-search-input"
                className="flex-1 rounded-full border border-[#0F3D2A]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#2D8659]"
              />
              <button
                onClick={searchPlace}
                disabled={busy || placeQuery.trim().length < 3}
                data-testid="farmers-loc-search-btn"
                className="rounded-full border border-[#0F3D2A]/20 bg-white px-3 py-2 hover:bg-black/5 disabled:opacity-40"
              >
                <MagnifyingGlass size={13} weight="bold" />
              </button>
            </div>
          </div>

          {placeResults.length > 0 && (
            <div className="rounded-2xl border border-[#0F3D2A]/15 bg-white p-2 space-y-1 max-h-52 overflow-y-auto">
              {placeResults.map((r, i) => (
                <button
                  key={r.place_id || i}
                  onClick={() => pickPlace(r)}
                  data-testid={`farmers-loc-search-result-${i}`}
                  className="w-full text-left rounded-xl px-3 py-2 hover:bg-[#0F3D2A]/5 text-sm text-[#0F3D2A]"
                >
                  {r.display_name}
                </button>
              ))}
            </div>
          )}

          {/* Country picker */}
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#0F3D2A] mb-1">
              Country
            </div>
            <div className="flex gap-1">
              <input
                value={countryQuery}
                onChange={(e) => setCountryQuery(e.target.value)}
                placeholder="Filter countries…"
                data-testid="farmers-loc-country-filter"
                className="flex-1 rounded-full border border-[#0F3D2A]/20 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#2D8659]"
              />
              {country && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#2D8659]/10 border border-[#2D8659]/25 px-3 py-1 text-sm text-[#0F3D2A]">
                  <Check size={12} weight="bold" /> {country}
                </span>
              )}
            </div>
            <div
              data-testid="farmers-loc-country-grid"
              className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-48 overflow-y-auto p-1 rounded-2xl border border-[#0F3D2A]/10 bg-white"
            >
              {filteredCountries.map((c) => {
                const active = country === c.name;
                return (
                  <button
                    key={c.code}
                    onClick={() => {
                      // Selecting a different country invalidates the
                      // previous state/city/village/coords — otherwise
                      // the pill reads e.g. "Portland, Kenya" and weather
                      // stays pinned to the old GPS fix.
                      if (c.name !== country) {
                        setCountry(c.name);
                        setState("");
                        setCity("");
                        setVillage("");
                        setLat(undefined);
                        setLon(undefined);
                      } else {
                        setCountry(c.name);
                      }
                    }}
                    data-testid={`farmers-loc-country-${c.code}`}
                    className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left transition-colors text-xs ${
                      active
                        ? "border-[#2D8659] bg-[#2D8659]/10 text-[#0F3D2A]"
                        : "border-transparent hover:border-[#0F3D2A]/20 text-[#0F3D2A]"
                    }`}
                  >
                    <span aria-hidden>{c.flag}</span>
                    <span className="truncate">{c.name}</span>
                  </button>
                );
              })}
              {filteredCountries.length === 0 && (
                <div className="col-span-full text-center text-xs text-[#0F3D2A]/60 py-4">
                  No matches. Use search above for other countries.
                </div>
              )}
            </div>
          </div>

          {/* State / City / Village */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field label="State / Region" value={state} setValue={setState} testid="farmers-loc-state" />
            <Field label="City / Town"    value={city}  setValue={setCity}  testid="farmers-loc-city" />
            <Field label="Village"        value={village} setValue={setVillage} testid="farmers-loc-village" />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              className="rounded-full border border-[#0F3D2A]/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#0F3D2A] hover:bg-black/5"
              data-testid="farmers-loc-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              data-testid="farmers-loc-set"
              className="rounded-full bg-[#0F3D2A] text-white px-6 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#2D8659]"
            >
              Set location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, setValue, testid }) {
  return (
    <label className="block">
      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#0F3D2A] mb-1">
        {label}
      </div>
      <input
        value={value || ""}
        onChange={(e) => setValue(e.target.value)}
        data-testid={testid}
        className="w-full rounded-xl border border-[#0F3D2A]/20 bg-white px-3 py-1.5 text-sm outline-none focus:border-[#2D8659]"
      />
    </label>
  );
}

/* ------------------- First-visit silent GPS bootstrap -------------------- */

/** Mount once at app level — silently fills the location if the user has
 *  already granted geolocation permission but hasn't picked a place yet. */
export function FarmersLocationAutoBootstrap() {
  const didRef = useRef(false);
  useEffect(() => {
    if (didRef.current) return;
    didRef.current = true;
    const cur = readLocation();
    // If we already have a real location (not the Portland default), skip.
    if (cur?.source && cur.source !== "default") return;
    if (!navigator.geolocation || !navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" }).then((p) => {
      if (p.state !== "granted") return; // never prompt automatically
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const { data } = await http.post("/nature/geo/lookup", {
            lat: pos.coords.latitude, lon: pos.coords.longitude,
          });
          writeLocation({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            country: data.country || null,
            state: data.state || null,
            city: data.city || data.locality || null,
            village: data.village || null,
            display_name: data.display_name,
            source: "gps",
          });
        } catch {}
      }, () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 3600000 });
    }).catch(() => {});
  }, []);
  return null;
}
